/**
 * API-klient og små hjælpere til brugerfladen.
 *
 * - Alle muterende kald sender CSRF-headeren automatisk.
 * - Fejl kommer tilbage som `ApiError` med en dansk besked og eventuelle
 *   feltfejl, så formularer kan vise dem det rigtige sted.
 * - Der caches ikke noget lokalt: personligt indhold hentes altid fra
 *   serveren, som svarer med `Cache-Control: no-store`.
 */

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: FieldError[];
  constructor(status: number, message: string, code = 'error', fields: FieldError[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
  fieldError(field: string): string | undefined {
    return this.fields.find((f) => f.field === field)?.message;
  }
}

let csrfToken = '';

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'Der er ingen forbindelse til serveren. Tjek dit netværk og prøv igen.');
  }

  const text = await response.text();
  let payload: Record<string, unknown> = {};
  if (text) {
    try {
      payload = JSON.parse(text) as Record<string, unknown>;
    } catch {
      payload = {};
    }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof payload.error === 'string' ? payload.error : 'Der opstod en fejl. Prøv igen.',
      typeof payload.code === 'string' ? payload.code : 'error',
      Array.isArray(payload.fields) ? (payload.fields as FieldError[]) : [],
    );
  }
  return payload as T;
}

export const api = {
  get: <T,>(path: string): Promise<T> => request<T>('GET', path),
  post: <T,>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body ?? {}),
  put: <T,>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, body ?? {}),
  del: <T,>(path: string, body?: unknown): Promise<T> => request<T>('DELETE', path, body),
};

/* ------------------------------------------------------------------ */
/* Dansk formatering i browseren                                       */
/* ------------------------------------------------------------------ */

const TZ = 'Europe/Copenhagen';
const LOCALE = 'da-DK';

export function formatTime(iso: string): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function formatDate(iso: string): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TZ,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso));
}

export function formatShortDate(iso: string): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TZ,
    day: 'numeric',
    month: 'short',
  }).format(new Date(iso));
}

export function formatDateTime(iso: string): string {
  if (!iso) return '';
  return `${formatDate(iso)} kl. ${formatTime(iso)}`;
}

/** «I dag», «I morgen», ellers en kort dansk dato. */
export function relativeDay(iso: string): string {
  if (!iso) return '';
  const target = dateKey(new Date(iso));
  const now = new Date();
  if (target === dateKey(now)) return 'I dag';
  const tomorrow = new Date(now.getTime() + 86400000);
  if (target === dateKey(tomorrow)) return 'I morgen';
  return formatShortDate(iso);
}

function dateKey(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d);
}

/** Dags dato som «YYYY-MM-DD» i foreningens tidszone. */
export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
}

/**
 * Afdelingens navn.
 *
 * Videresender til domænelaget, så etiketten står ÉT sted. Den forrige
 * udgave gættede «Gymnastik» på alt, den ikke kendte — og da badminton kom
 * til, ville den have kaldt badmintonholdene for gymnastik uden at fejle.
 */
export { departmentLabel } from '../../shared/domain.js';

/*
 * Rollens navn og beskrivelse, samme sted som afdelingsetiketten og af samme
 * grund. Administrationens «Din adgang» skrev dem tidligere selv med en
 * ternærkæde, der kun kendte `board` og `committee` — alt andet faldt
 * igennem til «Instruktør». En administrator fik derfor at vide, at han var
 * instruktør «med kun de hold, du er tildelt», mens han sad og så alle 54.
 * Et opslag kan mangle en nøgle og fejle synligt; en kæde gætter bare.
 */
export { isRole, roleLabel, ROLE_DESCRIPTIONS } from '../../shared/domain.js';

/**
 * «Mandag 17.00–18.00» — eller `null`, når holdet ingen træningstider har.
 *
 * Serveren sender `null` for et medlemskab. Den gamle kode læste felterne
 * direkte (`team.weekday[0].toUpperCase()`), og med `null` ville siden være
 * gået i stå med en tom skærm. Her er «ingen tider» et svar, ikke et
 * sammenbrud.
 */
export function scheduleText(team: {
  weekday: string | null;
  startTime: string | null;
  endTime: string | null;
}): string | null {
  if (!team.weekday || !team.startTime || !team.endTime) return null;
  return `${capitalise(team.weekday)} ${team.startTime}–${team.endTime}`;
}

/**
 * «1 deltager», «2 deltagere» — tal og navneord, der passer sammen.
 *
 * Findes fordi appen skrev «1 deltagere», «1 ulæste» og «1 træninger» flere
 * steder, og ét sted var lappet med «instruktør(er)». Ingen af delene er
 * forkerte oplysninger, men de får en app til at se uafsluttet ud netop
 * dér, hvor et menneske kigger efter noget om sig selv.
 *
 * Dansk flertal er uregelmæssigt, så begge former skrives ud. Et opslag,
 * der gætter endelsen, ville ramme «hold» og «træning» forskelligt og tage
 * fejl af mindst den ene.
 */
export function antal(n: number, ental: string, flertal: string): string {
  return `${n} ${n === 1 ? ental : flertal}`;
}

export function capitalise(value: string): string {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

/**
 * Kort, men ENTYDIGT navn til filterknapper.
 *
 * Fornavnet alene er ikke nok: to søskende deler ofte efternavn, og i en
 * familie kan to personer have samme fornavn. Kolliderer fornavnet med et
 * andet navn på listen, tilføjes næste navnedel.
 */
export function shortName(name: string, all: readonly string[]): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? name;
  const collisions = all.filter((other) => other.trim().split(/\s+/)[0] === first).length;
  if (collisions <= 1 || parts.length < 2) return first;
  return `${first} ${parts[1]}`;
}
